import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';

import api from '../api';

// ==================================================
// CONFIGURATION
// ==================================================

const API_PATH = '/todo-tasks';

const REFRESH_INTERVAL = 10000;

const COLORS = {
  background: '#0B1120',
  panel: '#0F172A',
  card: '#111827',
  border: '#1E293B',
  text: '#F8FAFC',
  secondary: '#94A3B8',
  muted: '#64748B',
  blue: '#2563EB',
  green: '#10B981',
  red: '#EF4444',
  white: '#FFFFFF',
};

const WEEKDAYS = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
];

// ==================================================
// MAIN COMPONENT
// ==================================================

export default function TodoTasksDropdown() {

  // --------------------------------------------------
  // CHECKLIST STATE
  // --------------------------------------------------

  const [open, setOpen] = useState(false);

  const [tasks, setTasks] = useState([]);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState('');

  const [canManage, setCanManage] = useState(false);

  const [savingId, setSavingId] = useState(null);

  const [currentDate, setCurrentDate] = useState('');

  // --------------------------------------------------
  // CREATE / EDIT STATE
  // --------------------------------------------------

  const [formOpen, setFormOpen] = useState(false);

  const [editingTask, setEditingTask] = useState(null);

  const [taskTitle, setTaskTitle] = useState('');

  const [taskDescription, setTaskDescription] = useState('');

  const [taskType, setTaskType] = useState('one_time');

  const [repeatDays, setRepeatDays] = useState([]);

  const [savingTask, setSavingTask] = useState(false);

  // --------------------------------------------------
  // TASK DETAILS
  // --------------------------------------------------

  const [selectedTask, setSelectedTask] = useState(null);

  // --------------------------------------------------
  // REQUEST CONTROL
  // --------------------------------------------------

  const loadingRef = useRef(false);

  const mutatingRef = useRef(false);

  const mountedRef = useRef(true);

  // ==================================================
  // ERROR HELPER
  // ==================================================

  const getErrorMessage = (
    err,
    fallback = 'Something went wrong.'
  ) => {

    return (
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      fallback
    );

  };

  // ==================================================
  // LOAD TASKS
  // ==================================================

  const loadTasks = useCallback(async (showLoader = false) => {

    if (
      loadingRef.current ||
      mutatingRef.current
    ) {
      return;
    }

    loadingRef.current = true;

    try {

      if (showLoader && mountedRef.current) {
        setLoading(true);
      }

      const response = await api.get(API_PATH, {
        params: {
          _: Date.now(),
        },

        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      const data = response.data;

      if (!mountedRef.current || mutatingRef.current) {
        return;
      }

      setTasks(
        Array.isArray(data.tasks)
          ? data.tasks
          : []
      );

      setCanManage(data.can_manage === true);

      setCurrentDate(data.date || '');

      setError('');

    } catch (err) {

      if (mountedRef.current) {

        setError(
          getErrorMessage(
            err,
            'Unable to load your tasks.'
          )
        );

      }

    } finally {

      loadingRef.current = false;

      if (mountedRef.current) {
        setLoading(false);
      }

    }

  }, []);

  // ==================================================
  // INITIAL LOAD + AUTOMATIC REFRESH
  // ==================================================

  useEffect(() => {

    mountedRef.current = true;

    loadTasks(true);

    const interval = setInterval(() => {

      if (
        document.visibilityState === 'visible' &&
        !mutatingRef.current
      ) {

        loadTasks();

      }

    }, REFRESH_INTERVAL);

    const handleVisibility = () => {

      if (document.visibilityState === 'visible') {
        loadTasks();
      }

    };

    document.addEventListener(
      'visibilitychange',
      handleVisibility
    );

    return () => {

      mountedRef.current = false;

      clearInterval(interval);

      document.removeEventListener(
        'visibilitychange',
        handleVisibility
      );

    };

  }, [loadTasks]);

  // ==================================================
  // REFRESH WHEN PANEL OPENS
  // ==================================================

  useEffect(() => {

    if (open) {
      loadTasks();
    }

  }, [open, loadTasks]);

  // ==================================================
  // CHECK OR UNCHECK TASK
  // ==================================================

  const toggleTask = async (task) => {

    if (mutatingRef.current) {
      return;
    }

    const completed = Number(task.is_completed) !== 1;

    mutatingRef.current = true;

    setSavingId(task.id);

    setError('');

    // Update checkbox immediately.

    setTasks(previous =>
      previous.map(item =>
        item.id === task.id
          ? {
              ...item,
              is_completed: completed ? 1 : 0,
            }
          : item
      )
    );

    try {

      await api.patch(
        `${API_PATH}/${task.id}/check`,
        {
          is_completed: completed,
        }
      );

    } catch (err) {

      setTasks(previous =>
        previous.map(item =>
          item.id === task.id
            ? {
                ...item,
                is_completed: task.is_completed,
              }
            : item
        )
      );

      setError(
        getErrorMessage(
          err,
          'Unable to update task.'
        )
      );

    } finally {

      mutatingRef.current = false;

      setSavingId(null);

      await loadTasks();

    }

  };

  // ==================================================
  // OPEN CREATE FORM
  // ==================================================

  const openCreateForm = () => {

    if (!canManage) {
      return;
    }

    setEditingTask(null);

    setTaskTitle('');

    setTaskDescription('');

    setTaskType('one_time');

    setRepeatDays([]);

    setSelectedTask(null);

    setFormOpen(true);

  };

  // ==================================================
  // OPEN EDIT FORM
  // ==================================================

  const openEditForm = (task) => {

    if (!canManage) {
      return;
    }

    setEditingTask(task);

    setTaskTitle(task.title);

    setTaskDescription(task.description || '');

    setTaskType(task.task_type);

    const days = String(task.repeat_days || '')
      .split(',')
      .filter(Boolean)
      .map(Number)
      .filter(day => day >= 0 && day <= 6);

    setRepeatDays(days);

    setSelectedTask(null);

    setFormOpen(true);

  };

  // ==================================================
  // CLOSE FORM
  // ==================================================

  const closeForm = () => {

    if (savingTask) {
      return;
    }

    setFormOpen(false);

    setEditingTask(null);

    setTaskTitle('');

    setTaskDescription('');

    setTaskType('one_time');

    setRepeatDays([]);

  };

  // ==================================================
  // SELECT WEEKDAY
  // ==================================================

  const toggleWeekday = (day) => {

    setRepeatDays(previous => {

      if (previous.includes(day)) {

        return previous.filter(
          value => value !== day
        );

      }

      return [...previous, day].sort(
        (a, b) => a - b
      );

    });

  };

  // ==================================================
  // CREATE OR EDIT TASK
  // ==================================================

  const saveTask = async () => {

    if (
      !canManage ||
      savingTask ||
      mutatingRef.current
    ) {
      return;
    }

    const title = taskTitle.trim();

    if (!title) {

      setError('Please enter a task name.');

      return;

    }

    if (
      taskType === 'weekly' &&
      repeatDays.length === 0
    ) {

      setError(
        'Please select at least one repeat day.'
      );

      return;

    }

    const payload = {

      title,

      description: taskDescription.trim(),

      task_type: taskType,

      repeat_days:
        taskType === 'weekly'
          ? [...repeatDays]
              .sort((a, b) => a - b)
              .join(',')
          : null,

    };

    mutatingRef.current = true;

    setSavingTask(true);

    setError('');

    try {

      if (editingTask) {

        await api.patch(
          `${API_PATH}/${editingTask.id}`,
          payload
        );

      } else {

        await api.post(
          API_PATH,
          payload
        );

      }

      closeFormAfterSave();

    } catch (err) {

      setError(
        getErrorMessage(
          err,
          'Unable to save task.'
        )
      );

    } finally {

      mutatingRef.current = false;

      setSavingTask(false);

      await loadTasks();

    }

  };

  // ==================================================
  // RESET FORM AFTER SUCCESSFUL SAVE
  // ==================================================

  const closeFormAfterSave = () => {

    setFormOpen(false);

    setEditingTask(null);

    setTaskTitle('');

    setTaskDescription('');

    setTaskType('one_time');

    setRepeatDays([]);

  };

  // ==================================================
  // ARCHIVE TASK
  // ==================================================

  const archiveTask = async (task) => {

    if (
      !canManage ||
      mutatingRef.current
    ) {
      return;
    }

    const confirmed = window.confirm(
      `Archive "${task.title}"?\n\n` +
      'This will remove the task from the active checklist.'
    );

    if (!confirmed) {
      return;
    }

    mutatingRef.current = true;

    setError('');

    try {

      await api.delete(
        `${API_PATH}/${task.id}`
      );

      setSelectedTask(null);

    } catch (err) {

      setError(
        getErrorMessage(
          err,
          'Unable to archive task.'
        )
      );

    } finally {

      mutatingRef.current = false;

      await loadTasks();

    }

  };

  // ==================================================
  // TASK COUNTS
  // ==================================================

  const completedCount = tasks.filter(
    task => Number(task.is_completed) === 1
  ).length;

  const pendingCount =
    tasks.length - completedCount;

  // ==================================================
  // TASK TYPE LABEL
  // ==================================================

  const getTaskTypeLabel = (task) => {

    if (task.task_type === 'daily') {
      return 'DAILY';
    }

    if (task.task_type === 'weekly') {
      return 'WEEKLY';
    }

    return 'ONE-TIME';

  };

  // ==================================================
  // MAIN COMPONENT
  // ==================================================

  return (
    <>

      {/* ============================================
          NAVIGATION BUTTON
      ============================================ */}

      <button
        type="button"
        onClick={() => setOpen(previous => !previous)}
        style={styles.navButton}
        title="Open To-Do List"
      >
        <span>✓</span>

        <span>To-Do</span>

        {pendingCount > 0 && (
          <span style={styles.navBadge}>
            {pendingCount}
          </span>
        )}
      </button>

      {/* ============================================
          MAIN CHECKLIST PANEL
      ============================================ */}

      {open && (

        <div
          style={styles.overlay}
          onMouseDown={event => {

            if (event.target === event.currentTarget) {
              setOpen(false);
            }

          }}
        >

          <div style={styles.panel}>

            {/* PANEL HEADER */}

            <div style={styles.header}>

              <div style={styles.headerText}>

                <h2 style={styles.title}>
                  To-Do List
                </h2>

                <div style={styles.subtitle}>
                  {pendingCount} remaining
                  {'  •  '}
                  {completedCount} completed
                </div>

              </div>

              <div style={styles.headerActions}>

                {canManage && (

                  <button
                    type="button"
                    onClick={openCreateForm}
                    style={styles.createButton}
                  >
                    + Create
                  </button>

                )}

                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  style={styles.closeButton}
                  title="Close"
                >
                  ✕
                </button>

              </div>

            </div>

            {/* ERROR */}

            {!!error && (

              <div style={styles.errorCard}>

                <div>
                  {error}
                </div>

                <button
                  type="button"
                  onClick={() => loadTasks()}
                  style={styles.retryButton}
                >
                  Retry
                </button>

              </div>

            )}

            {/* TASK LIST */}

            <div style={styles.taskList}>

              {loading && tasks.length === 0 ? (

                <div style={styles.loadingState}>
                  Loading tasks...
                </div>

              ) : tasks.length === 0 ? (

                <div style={styles.emptyState}>

                  <div style={styles.emptyIcon}>
                    ✓
                  </div>

                  <h3 style={styles.emptyTitle}>
                    No tasks yet
                  </h3>

                  <p style={styles.emptyDescription}>

                    {canManage
                      ? 'Tap Create to add your first task.'
                      : 'Your checklist is currently empty.'}

                  </p>

                  {canManage && (

                    <button
                      type="button"
                      onClick={openCreateForm}
                      style={styles.emptyCreateButton}
                    >
                      + Create Task
                    </button>

                  )}

                </div>

              ) : (

                tasks.map(task => {

                  const completed =
                    Number(task.is_completed) === 1;

                  const saving =
                    savingId === task.id;

                  return (

                    <div
                      key={task.id}
                      style={{
                        ...styles.taskRow,

                        ...(completed
                          ? styles.completedRow
                          : {}),
                      }}
                    >

                      {/* CHECKBOX */}

                      <button
                        type="button"
                        onClick={() => toggleTask(task)}
                        disabled={savingId !== null}
                        style={{
                          ...styles.checkbox,

                          ...(completed
                            ? styles.checkboxCompleted
                            : {}),
                        }}
                        title={
                          completed
                            ? 'Mark as incomplete'
                            : 'Mark as completed'
                        }
                        aria-label={
                          `${completed ? 'Uncheck' : 'Check'} ${task.title}`
                        }
                        aria-pressed={completed}
                      >

                        {saving ? (
                          '…'
                        ) : completed ? (
                          '✓'
                        ) : (
                          ''
                        )}

                      </button>

                      {/* TASK INFORMATION */}

                      <button
                        type="button"
                        onClick={() => setSelectedTask(task)}
                        style={styles.taskInformation}
                      >

                        <div
                          style={{
                            ...styles.taskTitle,

                            ...(completed
                              ? styles.taskTitleCompleted
                              : {}),
                          }}
                        >
                          {task.title}
                        </div>

                        {!!task.description && (

                          <div style={styles.taskDescription}>
                            {task.description}
                          </div>

                        )}

                        <div style={styles.taskMeta}>

                          <span style={styles.taskType}>
                            {getTaskTypeLabel(task)}
                          </span>

                          {completed && (

                            <span style={styles.completedLabel}>
                              Completed
                            </span>

                          )}

                        </div>

                      </button>

                      {/* TASK OPTIONS */}

                      {canManage && (

                        <button
                          type="button"
                          onClick={() => setSelectedTask(task)}
                          style={styles.optionsButton}
                          title="Task options"
                        >
                          ⋯
                        </button>

                      )}

                    </div>

                  );

                })

              )}

            </div>

            {/* PANEL FOOTER */}

            <div style={styles.footer}>

              <span>
                {currentDate || 'Shared Checklist'}
              </span>

              <button
                type="button"
                onClick={() => loadTasks()}
                style={styles.refreshButton}
              >
                ↻ Refresh
              </button>

            </div>

          </div>

        </div>

      )}

      {/* ============================================
          CREATE / EDIT MODAL
      ============================================ */}

      {open && formOpen && (

        <div style={styles.modalOverlay}>

          <div style={styles.modalCard}>

            {/* MODAL HEADER */}

            <div style={styles.modalHeader}>

              <div>

                <div style={styles.modalEyebrow}>
                  TASK MANAGEMENT
                </div>

                <h3 style={styles.modalTitle}>

                  {editingTask
                    ? 'Edit Task'
                    : 'Create Task'}

                </h3>

              </div>

              <button
                type="button"
                onClick={closeForm}
                disabled={savingTask}
                style={styles.closeButton}
              >
                ✕
              </button>

            </div>

            {/* FORM */}

            <div style={styles.formBody}>

              <label style={styles.inputLabel}>
                TASK NAME
              </label>

              <input
                type="text"
                value={taskTitle}
                onChange={event =>
                  setTaskTitle(event.target.value)
                }
                maxLength={255}
                placeholder="What needs to be done?"
                style={styles.input}
                autoFocus
              />

              <label style={styles.inputLabel}>
                DESCRIPTION
              </label>

              <textarea
                value={taskDescription}
                onChange={event =>
                  setTaskDescription(event.target.value)
                }
                placeholder="Additional instructions (optional)"
                rows={4}
                style={styles.textarea}
              />

              <label style={styles.inputLabel}>
                REPEAT SCHEDULE
              </label>

              <div style={styles.scheduleRow}>

                {[
                  {
                    value: 'one_time',
                    label: 'One-Time',
                  },
                  {
                    value: 'daily',
                    label: 'Daily',
                  },
                  {
                    value: 'weekly',
                    label: 'Weekly',
                  },
                ].map(option => (

                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setTaskType(option.value)
                    }
                    style={{
                      ...styles.scheduleButton,

                      ...(taskType === option.value
                        ? styles.scheduleButtonActive
                        : {}),
                    }}
                  >
                    {option.label}
                  </button>

                ))}

              </div>

              {/* WEEKLY DAY SELECTION */}

              {taskType === 'weekly' && (

                <div style={styles.weeklySection}>

                  <div style={styles.weeklyLabel}>
                    Select repeat days
                  </div>

                  <div style={styles.weekdaysRow}>

                    {WEEKDAYS.map(day => {

                      const selected =
                        repeatDays.includes(day.value);

                      return (

                        <button
                          key={day.value}
                          type="button"
                          onClick={() =>
                            toggleWeekday(day.value)
                          }
                          style={{
                            ...styles.weekdayButton,

                            ...(selected
                              ? styles.weekdayButtonActive
                              : {}),
                          }}
                        >
                          {day.label}
                        </button>

                      );

                    })}

                  </div>

                </div>

              )}

              {/* SCHEDULE DESCRIPTION */}

              <div style={styles.scheduleInfo}>

                {taskType === 'daily'
                  ? 'This task automatically appears unchecked every day.'
                  : taskType === 'weekly'
                    ? 'This task appears on the selected days each week.'
                    : 'This task stays completed until someone unchecks it.'}

              </div>

            </div>

            {/* FORM ACTIONS */}

            <div style={styles.modalActions}>

              <button
                type="button"
                onClick={closeForm}
                disabled={savingTask}
                style={styles.cancelButton}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={saveTask}
                disabled={savingTask}
                style={styles.saveButton}
              >

                {savingTask
                  ? 'Saving...'
                  : editingTask
                    ? 'Save Changes'
                    : 'Create Task'}

              </button>

            </div>

          </div>

        </div>

      )}

      {/* ============================================
          TASK DETAILS MODAL
      ============================================ */}

      {open && selectedTask && !formOpen && (

        <div style={styles.modalOverlay}>

          <div style={styles.detailsCard}>

            <div style={styles.modalHeader}>

              <h3 style={styles.modalTitle}>
                Task Details
              </h3>

              <button
                type="button"
                onClick={() => setSelectedTask(null)}
                style={styles.closeButton}
              >
                ✕
              </button>

            </div>

            <h3 style={styles.detailsTitle}>
              {selectedTask.title}
            </h3>

            <div style={styles.detailsBadge}>
              {getTaskTypeLabel(selectedTask)}
            </div>

            {!!selectedTask.description && (

              <div style={styles.detailsDescription}>
                {selectedTask.description}
              </div>

            )}

            <div style={styles.detailsActions}>

              <button
                type="button"
                onClick={() => setSelectedTask(null)}
                style={styles.cancelButton}
              >
                Close
              </button>

              {canManage && (

                <button
                  type="button"
                  onClick={() =>
                    openEditForm(selectedTask)
                  }
                  style={styles.saveButton}
                >
                  Edit Task
                </button>

              )}

            </div>

            {canManage && (

              <button
                type="button"
                onClick={() => archiveTask(selectedTask)}
                style={styles.archiveButton}
              >
                Archive Task
              </button>

            )}

          </div>

        </div>

      )}

    </>
  );

}

// ==================================================
// STYLES
// ==================================================

const styles = {

  // NAVIGATION BUTTON

  navButton: {
    border: '1px solid rgba(255,255,255,0.55)',
    background: 'transparent',
    color: '#F8FAFC',
    padding: '6px 12px',
    borderRadius: '6px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '38px',
    marginLeft: '8px',
    position: 'relative',
    whiteSpace: 'nowrap',
  },

  navBadge: {
    background: COLORS.blue,
    color: COLORS.white,
    borderRadius: '999px',
    padding: '2px 7px',
    fontSize: '11px',
    lineHeight: 1.2,
  },

  // MAIN PANEL

  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'rgba(2,6,23,0.65)',
    display: 'flex',
    justifyContent: 'flex-end',
  },

  panel: {
    width: 'min(560px, 100vw)',
    height: '100vh',
    background: COLORS.background,
    color: COLORS.text,
    boxShadow: '-24px 0 60px rgba(0,0,0,0.45)',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: `1px solid ${COLORS.border}`,
  },

  // HEADER

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '20px',
    borderBottom: `1px solid ${COLORS.border}`,
  },

  headerText: {
    flex: 1,
    minWidth: 0,
  },

  title: {
    margin: 0,
    color: COLORS.text,
    fontSize: '23px',
    fontWeight: 900,
  },

  subtitle: {
    marginTop: '5px',
    color: COLORS.secondary,
    fontSize: '12px',
    fontWeight: 600,
  },

  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  createButton: {
    border: 0,
    background: COLORS.blue,
    color: COLORS.white,
    borderRadius: '10px',
    padding: '10px 13px',
    fontSize: '12px',
    fontWeight: 800,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  closeButton: {
    border: `1px solid ${COLORS.border}`,
    background: COLORS.card,
    color: COLORS.text,
    borderRadius: '10px',
    width: '36px',
    height: '36px',
    fontSize: '15px',
    cursor: 'pointer',
    flexShrink: 0,
  },

  // TASK LIST

  taskList: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px',
  },

  taskRow: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '12px',
    padding: '11px 12px',
    marginBottom: '9px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '66px',
  },

  completedRow: {
    background: '#0C1E21',
    borderColor: '#14532D',
  },

  checkbox: {
    width: '27px',
    height: '27px',
    minWidth: '27px',
    borderRadius: '8px',
    border: `2px solid ${COLORS.muted}`,
    background: 'transparent',
    color: COLORS.white,
    fontSize: '17px',
    fontWeight: 900,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },

  checkboxCompleted: {
    background: COLORS.green,
    borderColor: COLORS.green,
  },

  taskInformation: {
    flex: 1,
    minWidth: 0,
    background: 'transparent',
    border: 0,
    textAlign: 'left',
    cursor: 'pointer',
    padding: '4px 0',
    color: COLORS.text,
  },

  taskTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: COLORS.text,
    overflowWrap: 'anywhere',
  },

  taskTitleCompleted: {
    color: COLORS.secondary,
    textDecoration: 'line-through',
  },

  taskDescription: {
    color: COLORS.secondary,
    fontSize: '12px',
    marginTop: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  taskMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '7px',
  },

  taskType: {
    color: COLORS.muted,
    fontSize: '10px',
    fontWeight: 800,
    letterSpacing: '0.5px',
  },

  completedLabel: {
    color: COLORS.green,
    fontSize: '11px',
    fontWeight: 700,
  },

  optionsButton: {
    background: 'transparent',
    border: 0,
    color: COLORS.secondary,
    fontSize: '23px',
    fontWeight: 800,
    cursor: 'pointer',
    width: '35px',
    height: '35px',
    flexShrink: 0,
  },

  // EMPTY / LOADING

  emptyState: {
    height: '100%',
    minHeight: '280px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '20px',
  },

  emptyIcon: {
    width: '65px',
    height: '65px',
    borderRadius: '20px',
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    color: COLORS.blue,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '30px',
    fontWeight: 900,
    marginBottom: '16px',
  },

  emptyTitle: {
    color: COLORS.text,
    fontSize: '20px',
    fontWeight: 900,
    marginBottom: '6px',
  },

  emptyDescription: {
    color: COLORS.secondary,
    fontSize: '12px',
    lineHeight: 1.6,
  },

  emptyCreateButton: {
    background: COLORS.blue,
    color: COLORS.white,
    border: 0,
    borderRadius: '10px',
    padding: '11px 18px',
    fontSize: '13px',
    fontWeight: 800,
    marginTop: '10px',
    cursor: 'pointer',
  },

  loadingState: {
    textAlign: 'center',
    color: COLORS.secondary,
    padding: '40px 10px',
    fontSize: '13px',
  },

  // FOOTER

  footer: {
    borderTop: `1px solid ${COLORS.border}`,
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    color: COLORS.secondary,
    fontSize: '12px',
  },

  refreshButton: {
    background: 'transparent',
    border: 0,
    color: COLORS.secondary,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 700,
  },

  // ERROR

  errorCard: {
    background: '#450A0A',
    border: `1px solid ${COLORS.red}`,
    borderRadius: '10px',
    margin: '12px',
    padding: '12px',
    color: '#FECACA',
    fontSize: '12px',
  },

  retryButton: {
    background: 'transparent',
    border: 0,
    color: COLORS.white,
    fontWeight: 800,
    cursor: 'pointer',
    marginTop: '8px',
    padding: 0,
  },

  // MODALS

  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 10001,
    background: 'rgba(0,0,0,0.78)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },

  modalCard: {
    width: 'min(480px, 100%)',
    maxHeight: '90vh',
    background: COLORS.panel,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '20px',
    padding: '20px',
    color: COLORS.text,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
  },

  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },

  modalEyebrow: {
    color: '#60A5FA',
    fontSize: '10px',
    fontWeight: 900,
    letterSpacing: '1px',
    marginBottom: '5px',
  },

  modalTitle: {
    margin: 0,
    color: COLORS.text,
    fontSize: '21px',
    fontWeight: 900,
  },

  formBody: {
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },

  inputLabel: {
    display: 'block',
    color: COLORS.secondary,
    fontSize: '10px',
    fontWeight: 900,
    letterSpacing: '1px',
    marginBottom: '8px',
  },

  input: {
    width: '100%',
    background: COLORS.background,
    border: `1px solid ${COLORS.border}`,
    color: COLORS.text,
    borderRadius: '10px',
    padding: '12px',
    marginBottom: '18px',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
  },

  textarea: {
    width: '100%',
    background: COLORS.background,
    border: `1px solid ${COLORS.border}`,
    color: COLORS.text,
    borderRadius: '10px',
    padding: '12px',
    marginBottom: '18px',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'vertical',
    minHeight: '90px',
  },

  // SCHEDULE

  scheduleRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '14px',
  },

  scheduleButton: {
    flex: 1,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '9px',
    padding: '11px 5px',
    color: COLORS.secondary,
    fontSize: '12px',
    fontWeight: 800,
    cursor: 'pointer',
  },

  scheduleButtonActive: {
    background: COLORS.blue,
    borderColor: COLORS.blue,
    color: COLORS.white,
  },

  weeklySection: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '12px',
    padding: '12px',
    marginBottom: '14px',
  },

  weeklyLabel: {
    color: COLORS.secondary,
    fontSize: '12px',
    fontWeight: 700,
    marginBottom: '10px',
  },

  weekdaysRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px',
  },

  weekdayButton: {
    flex: '1 1 12%',
    background: COLORS.background,
    border: `1px solid ${COLORS.border}`,
    color: COLORS.secondary,
    borderRadius: '8px',
    padding: '9px 3px',
    fontSize: '11px',
    fontWeight: 800,
    cursor: 'pointer',
  },

  weekdayButtonActive: {
    background: COLORS.blue,
    borderColor: COLORS.blue,
    color: COLORS.white,
  },

  scheduleInfo: {
    background: '#172554',
    borderRadius: '10px',
    padding: '12px',
    color: '#BFDBFE',
    fontSize: '12px',
    lineHeight: 1.6,
    marginBottom: '10px',
  },

  // MODAL ACTIONS

  modalActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '18px',
    paddingTop: '16px',
    borderTop: `1px solid ${COLORS.border}`,
  },

  cancelButton: {
    flex: 1,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    color: COLORS.text,
    padding: '12px',
    fontSize: '13px',
    fontWeight: 800,
    cursor: 'pointer',
  },

  saveButton: {
    flex: 1.3,
    background: COLORS.blue,
    border: 0,
    borderRadius: '10px',
    color: COLORS.white,
    padding: '12px',
    fontSize: '13px',
    fontWeight: 900,
    cursor: 'pointer',
  },

  // TASK DETAILS

  detailsCard: {
    width: 'min(480px, 100%)',
    maxHeight: '80vh',
    overflowY: 'auto',
    background: COLORS.panel,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '20px',
    padding: '20px',
    color: COLORS.text,
    boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
  },

  detailsTitle: {
    color: COLORS.text,
    fontSize: '20px',
    fontWeight: 900,
    marginBottom: '14px',
    overflowWrap: 'anywhere',
  },

  detailsBadge: {
    display: 'inline-block',
    background: '#172554',
    color: '#BFDBFE',
    borderRadius: '7px',
    padding: '6px 10px',
    fontSize: '10px',
    fontWeight: 800,
    marginBottom: '16px',
  },

  detailsDescription: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    padding: '12px',
    color: COLORS.secondary,
    fontSize: '13px',
    lineHeight: 1.6,
    marginBottom: '18px',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },

  detailsActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '12px',
  },

  archiveButton: {
    width: '100%',
    background: 'transparent',
    border: 0,
    color: COLORS.red,
    fontSize: '12px',
    fontWeight: 800,
    padding: '14px',
    marginTop: '12px',
    cursor: 'pointer',
  },

};
